import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import DeleteIcon from '@mui/icons-material/Delete';

interface SharedSigningProps {
  signatureCanvasRef: React.RefObject<HTMLCanvasElement>;
  signatureData: string;
  setSignatureData: React.Dispatch<React.SetStateAction<string>>;
  termsAndConditionsAccepted: boolean;
  setTermsAndConditionsAccepted: React.Dispatch<React.SetStateAction<boolean>>;
  accentColor?: string;
  compactSignature?: boolean;
  allowIncomplete?: boolean;
  showTermsReminder?: boolean;
}

type FullNameProps = {
  nameMode: 'full';
  signatureFullName: string;
  setSignatureFullName: React.Dispatch<React.SetStateAction<string>>;
  signatureFirstName?: never;
  setSignatureFirstName?: never;
  signatureLastName?: never;
  setSignatureLastName?: never;
};
type SplitNameProps = {
  nameMode?: 'split';
  signatureFullName?: never;
  setSignatureFullName?: never;
  signatureFirstName: string;
  setSignatureFirstName: React.Dispatch<React.SetStateAction<string>>;
  signatureLastName: string;
  setSignatureLastName: React.Dispatch<React.SetStateAction<string>>;
};
export type AgreementSigningProps = SharedSigningProps &
  (FullNameProps | SplitNameProps);

export default function AgreementSigningFields({
  signatureCanvasRef,
  signatureData,
  setSignatureData,
  nameMode = 'split',
  signatureFullName,
  setSignatureFullName,
  signatureFirstName,
  setSignatureFirstName,
  signatureLastName,
  setSignatureLastName,
  termsAndConditionsAccepted,
  setTermsAndConditionsAccepted,
  compactSignature = false,
  allowIncomplete = false,
  showTermsReminder = true,
  accentColor = '#e8bf21'
}: AgreementSigningProps) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const fullName =
    nameMode === 'full'
      ? signatureFullName.trim()
      : `${signatureFirstName.trim()} ${signatureLastName.trim()}`.trim();
  // Load cursive font for e-signature
  useEffect(() => {
    if (!document.getElementById('signature-font-link')) {
      const link = document.createElement('link');
      link.id = 'signature-font-link';
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const generateSignatureFromName = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!fullName) {
      setSignatureData('');
      return;
    }

    // Draw cursive text
    ctx.font = '54px "Dancing Script", cursive';
    if (compactSignature) {
      const size = Math.min(42, 54 * (canvas.width - 32) / Math.max(1, ctx.measureText(fullName).width));
      ctx.font = `${size}px "Dancing Script", cursive`;
    }
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      fullName,
      canvas.width / 2,
      canvas.height / 2,
      canvas.width - 32
    );

    // Save as data URL
    setSignatureData(canvas.toDataURL());
  };

  useEffect(() => {
    generateSignatureFromName();
  }, [fullName]);

  return (
    <>
      {/* Digital Signature Section */}
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center">
            <HistoryEduIcon fontSize="small" sx={{ color: '#666' }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#333' }}
            >
              Digital Signature
            </Typography>
          </Stack>
          <Button
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => {
              const canvas = signatureCanvasRef.current;
              if (canvas) {
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                setSignatureData('');
                if (nameMode === 'full') {
                  setSignatureFullName('');
                  setNameTouched(false);
                } else {
                  setSignatureFirstName('');
                  setSignatureLastName('');
                }
              }
            }}
            sx={{
              color: '#d32f2f',
              '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.04)' }
            }}
          >
            Clear
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Enter your {nameMode === 'full' ? 'full name' : 'first and last name'}{' '}
          to auto-generate a signature, or draw manually below.
        </Typography>

        {nameMode === 'full' ? (
          <TextField
            fullWidth
            required
            size="small"
            label="Full Name"
            placeholder="Full Name"
            autoComplete="name"
            inputProps={{ 'aria-label': 'Full Name', maxLength: 300 }}
            value={signatureFullName}
            onChange={(e) => setSignatureFullName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            error={nameTouched && !fullName}
            helperText={
              nameTouched && !fullName ? 'Full name is required.' : undefined
            }
            sx={{ mb: 2, '& input': { fontSize: { xs: 16, sm: 'inherit' } } }}
          />
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="First Name"
              inputProps={{ 'aria-label': 'First Name', maxLength: 150 }}
              value={signatureFirstName}
              onChange={(e) => setSignatureFirstName(e.target.value)}
              sx={{
                '& input': { fontSize: { xs: 16, sm: 'inherit' } },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fcfcfc',
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#bbb' }
                }
              }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder="Last Name"
              inputProps={{ 'aria-label': 'Last Name', maxLength: 150 }}
              value={signatureLastName}
              onChange={(e) => setSignatureLastName(e.target.value)}
              sx={{
                '& input': { fontSize: { xs: 16, sm: 'inherit' } },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fcfcfc',
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#bbb' }
                }
              }}
            />
          </Stack>
        )}

        <Paper
          elevation={0}
          sx={{
            maxWidth: compactSignature ? 420 : undefined,
            width: '100%',
            border: '1px dashed #bbb',
            borderRadius: 1,
            position: 'relative',
            backgroundColor: '#f9f9f9',
            cursor: 'crosshair',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: accentColor,
              bgcolor: '#fff'
            }
          }}
        >
          {!signatureData && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                opacity: 0.3,
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Sign Here
              </Typography>
            </Box>
          )}
          <canvas
            ref={signatureCanvasRef}
            width={compactSignature ? 420 : 700}
            height={compactSignature ? 100 : 120}
            style={{
              display: 'block',
              width: '100%',
              height: compactSignature ? 'auto' : '120px',
              touchAction: 'none'
            }}
            onMouseDown={(e) => {
              setIsDrawing(true);
              const canvas = signatureCanvasRef.current;
              if (!canvas) return;
              const ctx = canvas.getContext('2d');
              const rect = canvas.getBoundingClientRect();
              const scaleX = canvas.width / rect.width;
              const scaleY = canvas.height / rect.height;
              ctx.beginPath();
              ctx.moveTo(
                (e.clientX - rect.left) * scaleX,
                (e.clientY - rect.top) * scaleY
              );
            }}
            onMouseMove={(e) => {
              if (!isDrawing) return;
              const canvas = signatureCanvasRef.current;
              if (!canvas) return;
              const ctx = canvas.getContext('2d');
              const rect = canvas.getBoundingClientRect();
              const scaleX = canvas.width / rect.width;
              const scaleY = canvas.height / rect.height;
              ctx.lineWidth = 2.5;
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              ctx.strokeStyle = '#000';
              ctx.lineTo(
                (e.clientX - rect.left) * scaleX,
                (e.clientY - rect.top) * scaleY
              );
              ctx.stroke();
            }}
            onMouseUp={() => {
              setIsDrawing(false);
              const canvas = signatureCanvasRef.current;
              if (canvas) setSignatureData(canvas.toDataURL());
            }}
            onMouseLeave={() => {
              if (isDrawing) {
                setIsDrawing(false);
                const canvas = signatureCanvasRef.current;
                if (canvas) setSignatureData(canvas.toDataURL());
              }
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              setIsDrawing(true);
              const canvas = signatureCanvasRef.current;
              if (!canvas) return;
              const ctx = canvas.getContext('2d');
              const rect = canvas.getBoundingClientRect();
              const scaleX = canvas.width / rect.width;
              const scaleY = canvas.height / rect.height;
              const touch = e.touches[0];
              ctx.beginPath();
              ctx.moveTo(
                (touch.clientX - rect.left) * scaleX,
                (touch.clientY - rect.top) * scaleY
              );
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              if (!isDrawing) return;
              const canvas = signatureCanvasRef.current;
              if (!canvas) return;
              const ctx = canvas.getContext('2d');
              const rect = canvas.getBoundingClientRect();
              const scaleX = canvas.width / rect.width;
              const scaleY = canvas.height / rect.height;
              const touch = e.touches[0];
              ctx.lineWidth = 2.5;
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              ctx.strokeStyle = '#000';
              ctx.lineTo(
                (touch.clientX - rect.left) * scaleX,
                (touch.clientY - rect.top) * scaleY
              );
              ctx.stroke();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              setIsDrawing(false);
              const canvas = signatureCanvasRef.current;
              if (canvas) setSignatureData(canvas.toDataURL());
            }}
          />
        </Paper>
      </Stack>

      {/* Terms & Conditions Checkbox */}
      <Stack sx={{ borderTop: '1px solid #e0e0e0' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={termsAndConditionsAccepted}
              onChange={(e) => setTermsAndConditionsAccepted(e.target.checked)}
              sx={{
                '&.Mui-checked': {
                  color: '#000'
                }
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{}}>
              I agree to the <strong>Terms and Conditions</strong> and authorize
              this action.
            </Typography>
          }
        />
        {showTermsReminder && !termsAndConditionsAccepted && (
          <Typography sx={{ color: '#d32f2f', fontWeight: 500, mt: 0 }}>
            {allowIncomplete ? 'Review the Terms and Conditions. Continuing without acceptance will be recorded.' : '⚠️ You must accept the Terms and Conditions to proceed.'}
          </Typography>
        )}
      </Stack>
    </>
  );
}
